import React from "react"; // Keep React import
import {
  Home,
  ShoppingCart,
  UtensilsCrossed,
  Clock,
  Wrench,
  Users,
  BedDouble,
  Link2,
  MessageSquare,
  LineChart,
  BookOpen,
  Car,
  Wifi,
  CalendarCheck2,
  CreditCard,
  Settings,
  Cog,
  FileText,
  MonitorCheck,
} from "lucide-react";

// Define an interface for the navigation link items
export interface AdminNavLinkConfig {
  to: string;
  permission: string; // A single permission string
  icon: React.ReactNode; // Change to React.ReactNode
  textKey: string;
  defaultText: string;
  notificationCountKey?: string; // Optional key to fetch notification count from a store/context
  children?: AdminNavLinkConfig[]; // Optional nested child items
}

// Define required permissions for each link
export const navLinksConfig: AdminNavLinkConfig[] = [
  {
    to: "/admin/dashboard",
    permission: "admin:dashboard",
    icon: <Home />,
    textKey: "admin_nav_dashboard",
    defaultText: "Dashboard",
  },
  {
    to: "/admin/queue",
    permission: "admin:queue",
    icon: <Clock />,
    textKey: "admin_nav_queue",
    defaultText: "Live Queue",
  },
  {
    to: "/admin/front-desk",
    permission: "admin:reservations",
    icon: <MonitorCheck />,
    textKey: "admin_nav_front_desk",
    defaultText: "Front Desk",
  },
  {
    to: "/admin/orders",
    permission: "admin:foodorders:dashboard",
    icon: <ShoppingCart />,
    textKey: "admin_nav_food_orders",
    defaultText: "Food Orders",
    notificationCountKey: "newOrderCount",
  },
  {
    to: "/admin/menu",
    permission: "admin:menu",
    icon: <UtensilsCrossed />,
    textKey: "admin_nav_menu_management",
    defaultText: "Menu Management",
  },
  {
    to: "/admin/late-checkout",
    permission: "admin:latecheckouts",
    icon: <CalendarCheck2 />,
    textKey: "admin_nav_late_checkout_requests",
    defaultText: "Late Checkout",
    notificationCountKey: "newLateCheckoutCount",
  },
  {
    to: "/admin/service-requests",
    permission: "admin:servicerequests",
    icon: <Wrench />,
    textKey: "admin_nav_service_requests",
    defaultText: "Service Requests",
    notificationCountKey: "newServiceRequestCount",
  },
  {
    to: "/admin/service-types",
    permission: "admin:servicetypes",
    icon: <Cog />,
    textKey: "admin_nav_service_types",
    defaultText: "Service Types",
  },
  {
    to: "/admin/user-management",
    permission: "admin:usermanagement",
    icon: <Users />,
    textKey: "admin_nav_user_management",
    defaultText: "User Management",
  },
  {
    to: "/admin/housekeeping",
    permission: "admin:housekeeping",
    icon: <Wrench />,
    textKey: "admin_nav_housekeeping",
    defaultText: "Housekeeping",
  },
  {
    to: "/admin/rooms",
    permission: "admin:rooms",
    icon: <BedDouble />,
    textKey: "admin_nav_rooms",
    defaultText: "Rooms",
  },
  {
    to: "/admin/lock-connections",
    permission: "admin:rooms",
    icon: <Link2 />,
    textKey: "admin_nav_seam_connections",
    defaultText: "Lock Connections",
  },
  {
    to: "/admin/chat",
    permission: "admin:chat",
    icon: <MessageSquare />,
    textKey: "admin_nav_chat",
    defaultText: "Chat",
    notificationCountKey: "unreadChatCount",
  },
  {
    to: "/admin/payments",
    permission: "admin:payments:list",
    icon: <FileText />,
    textKey: "admin_nav_payments_list",
    defaultText: "Lista płatności",
  },
  {
    to: "/admin/payment-gateway",
    permission: "admin:paymentsettings",
    icon: <Settings />,
    textKey: "admin_nav_payment_gateway",
    defaultText: "Payment Gateway",
  },
  {
    to: "/admin/statistics",
    permission: "admin:statistics",
    icon: <LineChart />,
    textKey: "admin_nav_statistics",
    defaultText: "Statistics",
  },
  {
    to: "/admin/guest-guide",
    permission: "admin:guestguide",
    icon: <BookOpen />,
    textKey: "admin_nav_guest_guide",
    defaultText: "Guest Guide",
  },
  {
    to: "/admin/wifi",
    permission: "admin:guestwifi",
    icon: <Wifi />,
    textKey: "admin_nav_guest_wifi",
    defaultText: "Guest Wifi",
  },
  {
    to: "/admin/transfer-destinations",
    permission: "admin:transferdestinations",
    icon: <Car />,
    textKey: "admin_nav_transfer_destinations",
    defaultText: "Transfer Destinations",
  },
  {
    to: "/admin/transfer-requests",
    permission: "admin:transfers",
    icon: <Car />,
    textKey: "admin_nav_transfer_requests",
    defaultText: "Transfer Requests",
    notificationCountKey: "pendingTransferRequestCount",
  },
  {
    to: "/admin/reservations",
    permission: "admin:reservations",
    icon: <CalendarCheck2 />,
    textKey: "admin_nav_reservations",
    defaultText: "Reservations",
    notificationCountKey: "todayCheckinsCount",
  },
  {
    to: "/admin/pms-outbox",
    permission: "admin:pmsoutbox",
    icon: <Clock />,
    textKey: "admin_nav_pms_outbox",
    defaultText: "PMS Outbox",
  },
  {
    to: "/admin/billing",
    permission: "admin:billing",
    icon: <CreditCard />,
    textKey: "admin_nav_billing",
    defaultText: "Billing",
  },
];

// Helper function to get notification count based on key
// This can be used by components that consume navLinksConfig
export const getNotificationCount = (
  item: AdminNavLinkConfig,
  notificationSources: Record<string, number | undefined>,
): number | undefined => {
  if (
    item.notificationCountKey &&
    Object.prototype.hasOwnProperty.call(notificationSources, item.notificationCountKey)
  ) {
    return notificationSources[item.notificationCountKey];
  }
  return undefined;
};
